import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { ThemeProvider, CssBaseline, createTheme, Box } from '@mui/material';
import { DataGrid, GridRowId, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { enUS, trTR } from '@mui/material/locale';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import CustomFilterInput from './CustomFilterInput';
import { Checkbox } from 'antd';

const localeText = {
  en: {
    columnMenuSortAsc: 'Sort ascending',
    columnMenuSortDesc: 'Sort descending',
    columnMenuFilter: 'Filter',
    columnMenuColumns: 'Columns',
    columnMenuMore: 'More',
    paginationRowsPerPage: 'Rows per page',
    paginationTotalRows: 'Total rows',
    paginationRowsSelected: '{0} rows selected',
  },
  tr: {
    columnMenuSortAsc: 'Artan sıralama',
    columnMenuSortDesc: 'Azalan sıralama',
    columnMenuFilter: 'Filtrele',
    columnMenuColumns: 'Sütunlar',
    columnMenuMore: 'Daha fazla',
    paginationRowsPerPage: 'Sayfa başına satır',
    paginationTotalRows: 'Toplam satır',
    paginationRowsSelected: '{0} satır seçildi',
  },
};

export interface IMUIDataGridColumns {
  title: React.ReactNode;
  key: string;
  placeHolder: string;
  width?: number;
  render?: (order: any, record: any, index?: any) => React.ReactNode;
  isSorting?: boolean;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  dropdownItem?: any[];
  isSelected?: boolean;
  hideFilter?: boolean;
  hideColumnMenu?: boolean;
  enumValues?: { [key: string]: any }; // Add enumValues property
  dataType?: 'date' | 'text' | 'enum' | 'checkbox'; // Add dataType property
}

export interface MUIDatagridRef {
  GetAllData: () => any[];
}
export interface IDropdownItem {
  id: (number | string),
  name: string
}
interface MUIDataGridProps {
  data: Array<any>;
  columns: Array<IMUIDataGridColumns>;
  onChange: (filterInfo: {
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    filters?: { [key: string]: any };
  }) => void;
  total: number;
  onSelectionChange?: (selectedRows: any[]) => void;
  enableSelection?: boolean;
  defaultPageSize?: number;
  backgroundColor?: string;
  rowClassName?: (params: any) => string;
  filterMode?: 'client' | 'server';
  showStatusIcons?: boolean; // Add this new prop
  singleSelection?: boolean;
  onRowClick?: (rowData: any) => void;
}

const MUIDatagrid = forwardRef<MUIDatagridRef, MUIDataGridProps>((props, ref) => {
  const { data, columns, onSelectionChange, onChange, defaultPageSize = 50 } = props;
  const { i18n } = useTranslation();

  const [rows, setRows] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [dataGridInfo, setDataGridInfo] = useState<{
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    filters?: { [key: string]: any };
  }>({
    pageSize: defaultPageSize,
    page: 1,
  });

  useEffect(() => {
    setRows(data?.map((row, index) => ({ ...row, id: index })));
  }, [data]);

  useEffect(() => {
  },
    [props.total]);

  useImperativeHandle(ref, () => ({
    GetAllData: () => {
      onChange(dataGridInfo);
      const headerMapping = columns.reduce((acc, column) => {
        acc[column.key] = column.title;
        return acc;
      }, {} as Record<string, any>);

      return rows.map((row) => {
        const convertedRow: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          convertedRow[headerMapping[key] || key] = row[key];
        });
        return convertedRow;
      });
    },
  }));

  const theme = createTheme(
    {
      palette: {
        primary: { main: '#1976d2' },
      },
    },
    i18n.language === 'tr' ? trTR : enUS
  );

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const selectedRowsData = rows.filter((row) =>
      newSelection.includes(row.id)
    );

    setSelectedRows([...newSelection]);
    if (onSelectionChange) {
      onSelectionChange(selectedRowsData);
    }
  };

  const handlePageChange = (model: { page: number; pageSize: number }, sortModel?: any) => {
    const newPageInfo = {
      page: model.page + 1,
      pageSize: model.pageSize,
      sortField: sortModel?.field,
      sortOrder: sortModel?.sort,
      filters: dataGridInfo.filters,
    };
    setDataGridInfo(newPageInfo);
    onChange(newPageInfo);
  };

  const CheckboxFilterInput = (props: any) => {
    const { item, applyValue } = props;

    const handleChange = (event: any) => {
      applyValue({ ...item, value: event.target.checked });
    };

    return (
      <Checkbox
        checked={!!item.value}
        onChange={handleChange}
        style={{ marginLeft: 90, marginTop: 10 }}
      />
    );
  };


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: 'auto',
          width: '100%',
          marginTop: '30px',
          marginBottom: '30px',
          overflowY: 'auto',
          overflowX: 'auto',
          '& .super-app-theme--header': { backgroundColor: '#cd54471a' },
          '& .MuiDataGrid-columnHeaders': { fontWeight: 'bold' },
          '& .MuiDataGrid-checkboxInput': {
            color: '#cd54471a',
            '&.Mui-checked': { color: '#cd54471a' },
          },
          '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#007bff1a' },
          '& .row-red': {
            backgroundColor: '#ff4d4f !important',
            color: '#000',
          },
          '& .row-green': {
            backgroundColor: '#52c41a !important',
            color: '#000',
          },
          '& .row-white': {
            backgroundColor: 'white !important',
            color: '#000',
          },
          '& .status-column': {
            width: '50px',
            padding: '0 8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeader': {
            // Her zaman görünür olması için hover olmadan da göster
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              visibility: 'visible !important',
              padding: '0 8px'
            },
            '& .MuiDataGrid-menuIcon': {
              visibility: 'visible !important',
              width: 'auto'
            },
            '& .MuiDataGrid-iconButtonContainer': {
              visibility: 'visible !important',
              width: 'auto'
            },
            // Sort ve filter ikonlarını her zaman göster
            '& .MuiDataGrid-sortIcon': {
              opacity: '1 !important'
            },
            // Menu açma butonu her zaman görünür
            '& .MuiDataGrid-menuIconButton': {
              opacity: '1 !important'
            }
          },
          // Checkbox header'ı için özel stil
          '& .MuiDataGrid-columnHeader--checkbox': {
            width: '50px !important',
            // minWidth: '50px !important',
            //maxWidth: '50px !important',
            flex: '0 0 50px !important'
          }
        }}
      >
        <DataGrid
          rows={rows}
          rowCount={props.total}
          onRowClick={(params) => {
            props.onRowClick?.(params.row);
          }}
          columns={[
            ...(props.showStatusIcons ? [{
              field: 'status',
              headerName: '',
              headerClassName: 'super-app-theme--header',
              width: 40,
              sortable: false,
              filterable: false,
              renderCell: (params: any) => (
                <div className="status-column">
                  {params.row.backgroundColor === 'red' && (
                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px', marginTop: '15px' }} />
                  )}
                  {params.row.backgroundColor === 'green' && (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px', marginTop: '15px' }} />
                  )}

                  {!params.row.backgroundColor && 'isCompleted' in params.row && (
                    <>
                      {params.row.isCompleted ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px', marginTop: '15px' }} />
                      ) : (
                        <ClockCircleOutlined style={{ color: '#faad14', fontSize: '16px', marginTop: '15px' }} />
                      )}
                    </>
                  )}
                </div>
              ),
            }] : []),

            ...columns.map((col) => {
              return {
                ...col,
                field: col.key,
                headerName: i18n.t(col.title as string),
                headerClassName: 'super-app-theme--header',
                align: col.align || 'left',
                headerAlign: col.headerAlign || col.align || 'left',
                flex: col.width ? undefined : 0.5,
                minWidth: col.width ?? 100,
                // minWidth: col.width || 180,
                renderCell: (params: any) =>
                  col.render ? col.render(params.value, params.row, params.id) : params.value,
                sortable: col.isSorting ?? false,
                filterable: col.hideFilter !== true,
                disableColumnMenu: col.hideColumnMenu === true,
                filterOperators: col.dataType === 'checkbox' ? [
                  {
                    label: 'Seçili',
                    value: 'equals',
                    getApplyFilterFn: (filterItem: any) => {
                      if (filterItem.value == null) return null;
                      return (params: any) => params.value === filterItem.value;
                    },
                    InputComponent: CheckboxFilterInput,
                  },
                ] : [
                  {
                    label:
                      col.dataType === 'enum'
                        ? 'Eşittir'
                        : col.dataType === 'date'
                          ? 'Tarih Eşittir'
                          : 'İçerir',
                    value:
                      col.dataType === 'enum' || col.dataType === 'date'
                        ? 'equals'
                        : 'contains',
                    getApplyFilterFn: (filterItem: any) => {
                      if (!filterItem.value) return null;

                      return (params: any) => {
                        const cellValue = params?.value ?? '';
                        const filterValue = filterItem.value ?? '';

                        if (col.dataType === 'enum' || col.dataType === 'date') {
                          return cellValue === filterValue;
                        }

                        return String(cellValue)
                          .toLowerCase()
                          .includes(String(filterValue).toLowerCase());
                      };
                    },
                    InputComponent: (props: any) => (
                      <CustomFilterInput
                        {...props}
                        enumValues={col.enumValues}
                        dataType={col.dataType}
                      />
                    ),
                  },
                ],
              };
            })
          ]}
          getRowId={(row) => row.id}
          localeText={localeText[i18n.language as keyof typeof localeText]}
          paginationMode="server"
          filterMode={props.filterMode === 'server' ? 'server' : 'client'}
          paginationModel={{
            page: dataGridInfo.page - 1,
            pageSize: dataGridInfo.pageSize,
          }}
          onPaginationModelChange={(newModel) => handlePageChange(newModel)}
          onFilterModelChange={(newFilterModel) => {
            const updatedFilters: Record<string, any> = {};
            newFilterModel.items.forEach((item) => {
              if (item.value !== undefined && item.value !== null && item.value !== '' && item.field) {
                updatedFilters[item.field] = item.value;
              }
            });
            const newPageInfo = {
              ...dataGridInfo,
              page: 1,
              filters: updatedFilters,
            };

            setDataGridInfo(newPageInfo);
            onChange(newPageInfo);
          }}


          pageSizeOptions={[10, 20, 50, 100]}
          checkboxSelection={props.enableSelection}
          disableRowSelectionOnClick
          //rowSelectionModel={selectedRows}
          onRowSelectionModelChange={handleSelectionChange}
          getRowClassName={(params) => {
            if (params.row.backgroundColor) {
              return `row-${params.row.backgroundColor.toLowerCase()}`;
            }
            return 'row-white';
          }}
          isRowSelectable={() => true}
        />
      </Box>
    </ThemeProvider>
  );
});

export default MUIDatagrid;
